import type { ChoiceOption, ChoiceQuestion } from './polymersBiomolecules';

// Food chemistry and enzymes for the Polymers & Biomolecules Lab (`food-enzymes` mode).
// Activity curves are a deterministic teaching model with a stated shape, not measured data.

export const FOOD_DATA_META = {
  sources: [
    'IUBMB Enzyme Nomenclature: α-amylase (EC 3.2.1.1), pepsin A (EC 3.4.23.1), trypsin (EC 3.4.21.4) and triacylglycerol lipase (EC 3.1.1.3)',
    'Standard senior biology and chemistry texts: approximate optimum temperature and pH of digestive enzymes, denaturation and competitive inhibition',
    'Australian senior chemistry food-energy values: protein 17 kJ g⁻¹, carbohydrate 16 kJ g⁻¹, fat 37 kJ g⁻¹',
    'Food Standards Australia New Zealand (FSANZ) Food Standards Code, Schedule 11: energy factors used to calculate energy on Australian food labels',
    'NIST Chemistry WebBook: molecular formulae of sugars, amino acids, glycerol and fatty acids',
  ],
  conditions:
    'Enzyme optima are approximate textbook values. Activity curves are model shapes in relative units (peak = 100 %), not measurements. Food compositions are rounded illustrative values, not a specific product label.',
  reviewStatus: 'pending-teaching-review',
} as const;

// ---------------------------------------------------------------------------------------------
// Enzyme activity model
// ---------------------------------------------------------------------------------------------

export interface Enzyme {
  id: string;
  name: string;
  /** Where the enzyme works. */
  site: string;
  substrate: string;
  products: string;
  optimumTemperatureC: number;
  optimumPH: number;
  note: string;
}

export const ENZYMES: Enzyme[] = [
  {
    id: 'salivary-amylase',
    name: 'salivary amylase',
    site: 'the mouth, where saliva is close to neutral',
    substrate: 'starch',
    products: 'maltose and short glucose chains',
    optimumTemperatureC: 37,
    optimumPH: 6.8,
    note: 'Amylase keeps working on swallowed food only until stomach acid lowers the pH.',
  },
  {
    id: 'pepsin',
    name: 'pepsin',
    site: 'the stomach, where hydrochloric acid keeps the pH near 2',
    substrate: 'proteins',
    products: 'shorter polypeptides',
    optimumTemperatureC: 37,
    optimumPH: 2,
    note: 'Pepsin is unusual: its active site needs acidic conditions to work.',
  },
  {
    id: 'trypsin',
    name: 'trypsin',
    site: 'the small intestine, where pancreatic juice makes the pH slightly alkaline',
    substrate: 'proteins and polypeptides',
    products: 'shorter peptides',
    optimumTemperatureC: 37,
    optimumPH: 8,
    note: 'Trypsin is made by the pancreas and continues protein digestion after pepsin.',
  },
  {
    id: 'pancreatic-lipase',
    name: 'pancreatic lipase',
    site: 'the small intestine, where pancreatic juice makes the pH slightly alkaline',
    substrate: 'triglycerides (fats and oils)',
    products: 'fatty acids and glycerol (mostly a monoglyceride in the gut)',
    optimumTemperatureC: 37,
    optimumPH: 8,
    note: 'Bile emulsifies fat into small droplets so lipase has more surface to act on.',
  },
  {
    id: 'heat-stable-amylase',
    name: 'heat-stable bacterial α-amylase',
    site: 'starch processing vats used to make glucose syrup',
    substrate: 'starch',
    products: 'shorter glucose chains for syrup making',
    optimumTemperatureC: 90,
    optimumPH: 6.5,
    note: 'Enzymes from heat-loving bacteria keep their shape at temperatures that denature human enzymes.',
  },
];

/** Below the optimum, rate roughly doubles for every 10 °C rise (Q₁₀ ≈ 2 teaching rule). */
export const ACTIVITY_Q10 = 2;
const Q10_STEP_C = 10;
/** Above the optimum, the model loses all activity over this many degrees as the enzyme denatures. */
export const DENATURATION_SPAN_C = 15;
/** Width (standard deviation) of the model pH bell, in pH units. */
export const PH_BELL_WIDTH = 1.2;
export const PEAK_ACTIVITY = 100;

export const TEMPERATURE_RANGE = { min: 0, max: 100, step: 1 } as const;
export const PH_RANGE = { min: 0, max: 14, step: 0.5 } as const;

export function findEnzyme(id: string): Enzyme | null {
  return ENZYMES.find((item) => item.id === id) ?? null;
}

function inRange(value: number, range: { min: number; max: number }): boolean {
  return Number.isFinite(value) && value >= range.min && value <= range.max;
}

/** Relative activity (0–100 %) at an assumed optimum pH; null outside the modelled range. */
export function activityAtTemperature(enzyme: Enzyme, temperatureC: number): number | null {
  if (!inRange(temperatureC, TEMPERATURE_RANGE)) return null;
  const optimum = enzyme.optimumTemperatureC;
  if (temperatureC <= optimum) {
    return PEAK_ACTIVITY * ACTIVITY_Q10 ** ((temperatureC - optimum) / Q10_STEP_C);
  }
  return Math.max(0, PEAK_ACTIVITY * (1 - (temperatureC - optimum) / DENATURATION_SPAN_C));
}

/** Relative activity (0–100 %) at the optimum temperature; null outside pH 0–14. */
export function activityAtPH(enzyme: Enzyme, pH: number): number | null {
  if (!inRange(pH, PH_RANGE)) return null;
  const distance = pH - enzyme.optimumPH;
  return PEAK_ACTIVITY * Math.exp(-(distance * distance) / (2 * PH_BELL_WIDTH * PH_BELL_WIDTH));
}

export interface CurvePoint {
  x: number;
  y: number;
}

export function activityCurve(enzyme: Enzyme, variable: 'temperature' | 'ph'): CurvePoint[] {
  const range = variable === 'temperature' ? TEMPERATURE_RANGE : PH_RANGE;
  const points: CurvePoint[] = [];
  for (let x = range.min; x <= range.max; x += range.step) {
    const y = variable === 'temperature' ? activityAtTemperature(enzyme, x) : activityAtPH(enzyme, x);
    if (y !== null) points.push({ x, y });
  }
  return points;
}

/** Deterministic rotation so the correct option is not always first. */
function rotate<T>(items: T[], by: number): T[] {
  const shift = by % items.length;
  return [...items.slice(shift), ...items.slice(0, shift)];
}

export const PH_BANDS = [
  { id: 'acidic', label: 'Acidic, about pH 2', pH: 2 },
  { id: 'neutral', label: 'Close to neutral, about pH 7', pH: 7 },
  { id: 'alkaline', label: 'Slightly alkaline, about pH 8', pH: 8 },
  { id: 'strong-alkaline', label: 'Strongly alkaline, about pH 12', pH: 12 },
] as const;

function nearestBand(pH: number) {
  return PH_BANDS.reduce((best, band) =>
    Math.abs(band.pH - pH) < Math.abs(best.pH - pH) ? band : best,
  );
}

/** "Which pH suits this enzyme?" built from the enzyme's optimum; null for an unknown id. */
export function enzymePHQuestion(enzymeId: string): ChoiceQuestion | null {
  const index = ENZYMES.findIndex((item) => item.id === enzymeId);
  if (index < 0) return null;
  const enzyme = ENZYMES[index];
  const best = nearestBand(enzyme.optimumPH);
  const options: ChoiceOption[] = PH_BANDS.map((band) => {
    if (band.id === best.id) {
      return {
        id: band.id,
        label: band.label,
        correct: true,
        feedback: `Correct. ${enzyme.name} works in ${enzyme.site}; the model peaks at pH ${enzyme.optimumPH}. ${enzyme.note}`,
      };
    }
    const activity = Math.round(activityAtPH(enzyme, band.pH) ?? 0);
    const reason =
      band.id === 'strong-alkaline'
        ? 'No digestive enzyme works at pH 12: the R groups around the active site lose or gain H⁺, ionic and hydrogen bonds change and the active site loses its shape.'
        : `At about pH ${band.pH} the model gives only ${activity} % activity. A pH away from the optimum changes the charges on R groups in the active site, so the substrate fits less well.`;
    return {
      id: band.id,
      label: band.label,
      correct: false,
      feedback: `${reason} ${enzyme.name} works in ${enzyme.site} (optimum about pH ${enzyme.optimumPH}).`,
    };
  });
  return {
    id: `ph-${enzyme.id}`,
    prompt: `At which pH will ${enzyme.name} work fastest? Think about where it works.`,
    options: rotate(options, index),
  };
}

export const COMPARISON_TEMPERATURE_C = 37;
export const HOT_TEMPERATURE_C = 60;
/** Ratios used to classify the model change between 37 °C and 60 °C. */
const MORE_ACTIVE_RATIO = 1.1;
const DENATURED_RATIO = 0.5;

export type TemperatureOutcome = 'more' | 'same' | 'denatured';

export function temperatureOutcome(enzyme: Enzyme): TemperatureOutcome {
  const warm = activityAtTemperature(enzyme, COMPARISON_TEMPERATURE_C)!;
  const hot = activityAtTemperature(enzyme, HOT_TEMPERATURE_C)!;
  if (hot >= warm * MORE_ACTIVE_RATIO) return 'more';
  if (hot <= warm * DENATURED_RATIO) return 'denatured';
  return 'same';
}

const TEMPERATURE_OPTIONS: { id: TemperatureOutcome; label: string }[] = [
  { id: 'more', label: 'More active: particles collide more often and with more energy' },
  { id: 'same', label: 'About the same: temperature does not affect enzymes' },
  { id: 'denatured', label: 'Much less active: the enzyme has denatured' },
];

/** "Compare 37 °C with 60 °C", answered by the temperature model; null for an unknown id. */
export function enzymeTemperatureQuestion(enzymeId: string): ChoiceQuestion | null {
  const index = ENZYMES.findIndex((item) => item.id === enzymeId);
  if (index < 0) return null;
  const enzyme = ENZYMES[index];
  const outcome = temperatureOutcome(enzyme);
  const warm = Math.round(activityAtTemperature(enzyme, COMPARISON_TEMPERATURE_C)!);
  const hot = Math.round(activityAtTemperature(enzyme, HOT_TEMPERATURE_C)!);
  const modelText = `The model gives ${warm} % at ${COMPARISON_TEMPERATURE_C} °C and ${hot} % at ${HOT_TEMPERATURE_C} °C (optimum about ${enzyme.optimumTemperatureC} °C).`;
  const feedback: Record<TemperatureOutcome, string> = {
    more:
      outcome === 'more'
        ? `Correct. ${HOT_TEMPERATURE_C} °C is still below this enzyme's optimum, so more frequent, more energetic collisions speed it up. ${modelText}`
        : `Faster collisions only help below the optimum. Above about ${enzyme.optimumTemperatureC} °C the bonds holding the tertiary structure break and the active site changes shape. ${modelText}`,
    same: `Temperature changes both collision rate and enzyme shape, so activity is never constant across this range. ${modelText}`,
    denatured:
      outcome === 'denatured'
        ? `Correct. ${HOT_TEMPERATURE_C} °C is well above the optimum: hydrogen bonds and other forces holding the tertiary structure break, the active site changes shape and the substrate no longer fits. Peptide bonds are not broken. ${modelText}`
        : `This enzyme comes from heat-loving bacteria, so its structure is stable at ${HOT_TEMPERATURE_C} °C; it has not reached its optimum yet. ${modelText}`,
  };
  return {
    id: `temperature-${enzyme.id}`,
    prompt: `Compared with ${COMPARISON_TEMPERATURE_C} °C, how active is ${enzyme.name} at ${HOT_TEMPERATURE_C} °C?`,
    options: rotate(
      TEMPERATURE_OPTIONS.map((option) => ({
        id: option.id,
        label: option.label,
        correct: option.id === outcome,
        feedback: feedback[option.id],
      })),
      index,
    ),
  };
}

export interface ActivityCase {
  id: string;
  enzymeId: string;
  variable: 'temperature' | 'ph';
}

/** Every enzyme is asked about both variables: 2 × 5 = 10 activity cases. */
export const ACTIVITY_CASES: ActivityCase[] = ENZYMES.flatMap((enzyme) => [
  { id: `ph-${enzyme.id}`, enzymeId: enzyme.id, variable: 'ph' as const },
  { id: `temperature-${enzyme.id}`, enzymeId: enzyme.id, variable: 'temperature' as const },
]);

export function activityQuestion(item: ActivityCase): ChoiceQuestion | null {
  return item.variable === 'ph'
    ? enzymePHQuestion(item.enzymeId)
    : enzymeTemperatureQuestion(item.enzymeId);
}

// ---------------------------------------------------------------------------------------------
// Competitive inhibition (qualitative Michaelis–Menten shape, arbitrary units)
// ---------------------------------------------------------------------------------------------

export const INHIBITION_MODEL = {
  maxRate: 100,
  /** Substrate concentration giving half the maximum rate without inhibitor (arbitrary units). */
  halfRateSubstrate: 2,
  /** A fixed amount of competitive inhibitor makes the half-rate substrate four times larger. */
  inhibitorFactor: 4,
  substrateMax: 40,
  substrateStep: 0.5,
} as const;

export function inhibitedRate(substrate: number, inhibitor: boolean): number | null {
  if (!Number.isFinite(substrate) || substrate < 0 || substrate > INHIBITION_MODEL.substrateMax) {
    return null;
  }
  const half =
    INHIBITION_MODEL.halfRateSubstrate * (inhibitor ? INHIBITION_MODEL.inhibitorFactor : 1);
  return (INHIBITION_MODEL.maxRate * substrate) / (half + substrate);
}

export function inhibitionCurve(inhibitor: boolean): CurvePoint[] {
  const points: CurvePoint[] = [];
  for (let x = 0; x <= INHIBITION_MODEL.substrateMax; x += INHIBITION_MODEL.substrateStep) {
    points.push({ x, y: inhibitedRate(x, inhibitor)! });
  }
  return points;
}

export const INHIBITION_QUESTION: ChoiceQuestion = {
  id: 'competitive-inhibition',
  prompt:
    'A competitive inhibitor has a shape like the substrate. If you keep the inhibitor amount fixed and add much more substrate, what happens to the rate?',
  options: [
    {
      id: 'approaches-max',
      label: 'It climbs towards the same maximum rate as without the inhibitor',
      correct: true,
      feedback:
        'Correct. The inhibitor and substrate compete for the same active site. With far more substrate molecules, substrate wins most collisions, so the rate approaches the same maximum; it just needs more substrate to get there.',
    },
    {
      id: 'lower-max',
      label: 'It levels off at a permanently lower maximum',
      correct: false,
      feedback:
        'A lower maximum happens when an inhibitor binds somewhere else and changes the active site (non-competitive inhibition). A competitive inhibitor can be out-competed, so the curve rises towards the same maximum.',
    },
    {
      id: 'zero',
      label: 'It drops to zero because every active site is blocked',
      correct: false,
      feedback:
        'Competitive inhibitors bind reversibly, so active sites keep freeing up. Extra substrate takes more of those free sites, so the rate rises, not falls.',
    },
    {
      id: 'denatured',
      label: 'It falls because the inhibitor denatures the enzyme',
      correct: false,
      feedback:
        'A competitive inhibitor does not change the enzyme’s shape. It only occupies the active site for a while, and extra substrate competes it away.',
    },
  ],
};
